/**
 * Minimal reactive store. ~300 bytes minified.
 * `get` returns current value; `set` shallow-merges + notifies; `subscribe`
 * returns an unsubscribe.
 */
export interface Store<T> {
  get(): T;
  set(patch: Partial<T> | ((cur: T) => Partial<T>)): void;
  replace(next: T): void;
  subscribe(fn: (state: T) => void): () => void;
}

export function createStore<T extends object>(initial: T): Store<T> {
  let state = initial;
  const subs = new Set<(s: T) => void>();
  const notify = () => {
    for (const fn of subs) fn(state);
  };
  return {
    get: () => state,
    set(patch) {
      const next = typeof patch === 'function' ? patch(state) : patch;
      state = { ...state, ...next };
      notify();
    },
    replace(next) {
      state = next;
      notify();
    },
    subscribe(fn) {
      subs.add(fn);
      return () => subs.delete(fn);
    },
  };
}
